import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  useTheme,
  styled,
  alpha,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
} from '@mui/material'
import {
   Handshake as HandshakeIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  BugReport as BugReportIcon,
  Gavel as GavelIcon,
  MenuBook as BookIcon,
  Article as ArticleIcon,
  Description as DocumentIcon,
  ContactSupport as ContactIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'

// Styled components with legal theme
// const AnimatedContainer = styled(Container)(({ theme }) => ({
//   minHeight: '80vh',
//   display: 'flex',
//   flexDirection: 'column',
//   justifyContent: 'center',
//   alignItems: 'center',
//   textAlign: 'center',
//   padding: theme.spacing(2),
//   position: 'relative',
//   overflow: 'hidden',
//   background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
// }))
const AnimatedContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  // Ensure full coverage
  margin: 0,
  boxSizing: 'border-box',
}))

const FloatingElement = styled(Box)(({ theme, delay = 0 }) => ({
  position: 'absolute',
  opacity: 0.08,
  animation: `float 8s ease-in-out infinite ${delay}s`,
  color: '#232536',
  pointerEvents: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'none', // Hide floating elements on mobile for cleaner look
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px) rotate(0deg)',
    },
    '50%': {
      transform: 'translateY(-30px) rotate(3deg)',
    },
  },
}))

// Responsive broken 404 with legal theme colors
const BrokenErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  perspective: '1000px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
}))

const ErrorDigit = styled(Typography)(({ theme, position, ismobile }) => {
  const baseStyles = {
    fontSize: 'clamp(4rem, 12vw, 8rem)',
    fontWeight: 900,
    lineHeight: 0.9,
    textShadow: '0 4px 20px rgba(35, 37, 54, 0.2)',
    fontFamily: '"Inter", "Roboto", sans-serif',
    userSelect: 'none',
  }

  // Theme-appropriate colors for legal platform
  const getColorAndAnimation = () => {
    switch (position) {
      case 'first':
        return {
          color: '#232536', // Primary dark
          animation: ismobile ? 'none' : 'tiltLeft 3s ease-in-out infinite',
          transform: ismobile ? 'none' : 'rotateY(-10deg)',
        }
      case 'zero':
        return {
          background: 'linear-gradient(135deg, #ffcf51 0%, #ff9800 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'pulse 2s ease-in-out infinite',
          transform: 'scale(1.1)',
        }
      case 'last':
        return {
          color: '#666666',
          animation: ismobile
            ? 'slideInBroken 1s ease-out'
            : 'disconnect 4s ease-in-out infinite',
          transform: ismobile
            ? 'translateX(0)'
            : 'rotateY(10deg) translateX(10px)',
          opacity: ismobile ? 1 : 0.8,
        }
      default:
        return { color: '#232536' }
    }
  }

  return {
    ...baseStyles,
    ...getColorAndAnimation(),
    '@keyframes tiltLeft': {
      '0%, 100%': { transform: 'rotateY(-10deg)' },
      '50%': { transform: 'rotateY(-15deg) translateX(-5px)' },
    },
    '@keyframes disconnect': {
      '0%, 60%': {
        transform: 'rotateY(10deg) translateX(10px)',
        opacity: 0.8,
      },
      '70%, 90%': {
        transform: 'rotateY(20deg) translateX(20px)',
        opacity: 0.4,
      },
      '100%': {
        transform: 'rotateY(10deg) translateX(10px)',
        opacity: 0.8,
      },
    },
    '@keyframes pulse': {
      '0%, 100%': { transform: 'scale(1.1)' },
      '50%': { transform: 'scale(1.2)' },
    },
    '@keyframes slideInBroken': {
      '0%': {
        transform: 'translateY(50px)',
        opacity: 0,
      },
      '100%': {
        transform: 'translateY(0)',
        opacity: 1,
      },
    },
  }
})

const ConnectionLine = styled(Box)(({ theme, ismobile, position }) => ({
  width: ismobile ? '80%' : '50px',
  height: ismobile ? '4px' : '8px',
  background: 'linear-gradient(90deg, #ffcf51 0%, #ff9800 50%, #666666 100%)',
  margin: ismobile ? theme.spacing(1, 0) : theme.spacing(0, 1.5),
  borderRadius: '4px',
  position: 'relative',
  overflow: 'hidden',
  animation: ismobile
    ? 'mobileWireBreak 3s ease-in-out infinite'
    : 'wireBreak 4s ease-in-out infinite',
  animationDelay: position === 'second' ? '0.5s' : '0s',

  // Main wire glow effect
  boxShadow:
    '0 0 10px rgba(255, 207, 81, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)',

  // Multiple spark effects
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    animation: 'multiSpark 2s ease-in-out infinite',
  },

  // Main spark
  '&::before': {
    top: '50%',
    left: '45%',
    width: '12px',
    height: '12px',
    background:
      'radial-gradient(circle, #fff 0%, #ffcf51 30%, #ff6b6b 70%, transparent 100%)',
    transform: 'translate(-50%, -50%)',
    boxShadow: '0 0 20px #ff6b6b, 0 0 40px #ffcf51',
    animationDelay: '0.2s',
  },

  // Secondary spark
  '&::after': {
    top: '50%',
    right: '45%',
    width: '8px',
    height: '8px',
    background:
      'radial-gradient(circle, #fff 0%, #ff9800 40%, #ff6b6b 80%, transparent 100%)',
    transform: 'translate(50%, -50%)',
    boxShadow: '0 0 15px #ff9800',
    animationDelay: '0.7s',
  },

  '@keyframes wireBreak': {
    '0%, 30%': {
      background: 'linear-gradient(90deg, #ffcf51 0%, #ff9800 100%)',
      boxShadow: '0 0 10px rgba(255, 207, 81, 0.5)',
    },
    '35%': {
      background:
        'linear-gradient(90deg, #ffcf51 0%, #ff6b6b 50%, transparent 55%, transparent 100%)',
      boxShadow: '0 0 20px rgba(255, 107, 107, 0.8)',
    },
    '40%, 80%': {
      background:
        'linear-gradient(90deg, #ffcf51 0%, transparent 45%, transparent 55%, #666666 100%)',
      boxShadow: '0 0 5px rgba(255, 207, 81, 0.3)',
    },
    '85%': {
      background:
        'linear-gradient(90deg, #ffcf51 0%, #ff6b6b 50%, transparent 55%, transparent 100%)',
      boxShadow: '0 0 20px rgba(255, 107, 107, 0.8)',
    },
    '100%': {
      background: 'linear-gradient(90deg, #ffcf51 0%, #ff9800 100%)',
      boxShadow: '0 0 10px rgba(255, 207, 81, 0.5)',
    },
  },

  '@keyframes mobileWireBreak': {
    '0%, 25%': {
      background: 'linear-gradient(90deg, #ffcf51 0%, #ff9800 100%)',
      opacity: 1,
      transform: 'scaleX(1)',
    },
    '30%': {
      background:
        'linear-gradient(90deg, #ffcf51 0%, #ff6b6b 50%, transparent 60%, transparent 100%)',
      opacity: 0.8,
      transform: 'scaleX(0.9)',
    },
    '35%, 75%': {
      background:
        'linear-gradient(90deg, #ffcf51 0%, transparent 40%, transparent 60%, #666666 100%)',
      opacity: 0.6,
      transform: 'scaleX(0.8)',
    },
    '80%': {
      background:
        'linear-gradient(90deg, #ffcf51 0%, #ff6b6b 50%, transparent 60%, transparent 100%)',
      opacity: 0.8,
      transform: 'scaleX(0.9)',
    },
    '100%': {
      background: 'linear-gradient(90deg, #ffcf51 0%, #ff9800 100%)',
      opacity: 1,
      transform: 'scaleX(1)',
    },
  },

  '@keyframes multiSpark': {
    '0%, 25%, 75%, 100%': {
      opacity: 0,
      transform: 'translate(-50%, -50%) scale(0) rotate(0deg)',
    },
    '30%, 35%': {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1.5) rotate(45deg)',
    },
    '32%': {
      opacity: 0.8,
      transform: 'translate(-50%, -50%) scale(0.8) rotate(90deg)',
    },
    '80%, 85%': {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1.2) rotate(-45deg)',
    },
    '82%': {
      opacity: 0.6,
      transform: 'translate(-50%, -50%) scale(0.6) rotate(-90deg)',
    },
  },
}))

// Add electrical discharge effect component
const ElectricalDischarge = styled(Box)(({ theme, ismobile }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '100px',
  height: '2px',
  background: 'transparent',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '40%',
    width: '20%',
    height: '6px',
    background: '#fff',
    borderRadius: '3px',
    boxShadow: '0 0 15px #ffcf51, 0 0 30px #ff6b6b',
    animation: 'lightning 4s ease-in-out infinite',
    opacity: 0,
  },

  '@keyframes lightning': {
    '0%, 30%, 75%, 100%': {
      opacity: 0,
      transform: 'scaleX(0)',
    },
    '35%': {
      opacity: 1,
      transform: 'scaleX(1) scaleY(2)',
    },
    '36%': {
      opacity: 0.5,
      transform: 'scaleX(0.5) scaleY(1)',
    },
    '37%': {
      opacity: 1,
      transform: 'scaleX(1.2) scaleY(3)',
    },
    '80%': {
      opacity: 1,
      transform: 'scaleX(0.8) scaleY(2)',
    },
    '81%': {
      opacity: 0.3,
      transform: 'scaleX(0.3) scaleY(1)',
    },
  },
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: 700,
  width: '100%',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  border: `1px solid ${alpha('#232536', 0.1)}`,
  boxShadow: '0 20px 60px rgba(35, 37, 54, 0.1)',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    margin: theme.spacing(0, 2),
  },
}))

const ActionButton = styled(Button)(({ theme, variant }) => ({
  margin: theme.spacing(1),
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minWidth: '140px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '120px',
    padding: theme.spacing(1, 2),
    margin: theme.spacing(0.5),
  },
  ...(variant === 'primary' && {
    background: 'linear-gradient(135deg, #232536 0%, #3a3e5a 100%)',
    color: 'white',
    border: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #3a3e5a 0%, #232536 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(35, 37, 54, 0.2)',
    },
  }),
  ...(variant === 'secondary' && {
    borderColor: '#ffcf51',
    color: '#232536',
    backgroundColor: 'rgba(255, 207, 81, 0.1)',
    '&:hover': {
      backgroundColor: '#ffcf51',
      color: '#232536',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(255, 207, 81, 0.3)',
    },
  }),
  ...(variant === 'outline' && {
    borderColor: '#232536',
    color: '#232536',
    '&:hover': {
      backgroundColor: alpha('#232536', 0.05),
      transform: 'translateY(-1px)',
    },
  }),
}))

const SuggestionCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha('#232536', 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 35px rgba(35, 37, 54, 0.15)',
    borderColor: '#ffcf51',
    '& .suggestion-icon': {
      transform: 'scale(1.1)',
      color: '#ffcf51',
    },
  },
}))

const Error404 = ({
  error,
  articleSlug,
  customTitle,
  showSuggestions = true,
  showErrorDetails = false,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const suggestions = [
    {
      icon: <HandshakeIcon />,
      title: 'Partner',
      description: (
        <>
          Collaborate with us
          {/* <br /> */}
        </>
      ),
      action: () => window.open('mailto:partnerships@gcctaxlaws.com'),
      color: '#666666', //'#ff6b6b',
    },
    {
      icon: <HomeIcon />,
      title: 'Home',
      description: 'Return to homepage',
      action: () => navigate('/'),
      color: '#666666', //'#232536',
    },
    {
      icon: <SearchIcon />,
      title: 'Search Laws',
      description: 'Find legal documents',
      action: () => navigate('/search-across-law'),
      color: '#666666', //'#ffcf51',
    },
    {
      icon: <BookIcon />,
      title: 'Browse Laws',
      description: 'Explore law library',
      action: () => navigate('/laws/uae/cit-fdl-47-of-2022'),
      color: '#666666',
    },
    {
      icon: <ContactIcon />,
      title: 'Support',
      description: 'Contact our team',
      action: () => window.open('mailto:support@gcctaxlaws.com'),
      color: '#666666', //'#ff6b6b',
    },
  ]

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  const handleRefresh = () => {
    window.location.reload()
  }

  const handleReportIssue = () => {
    const subject = encodeURIComponent(
      `404 Error Report - ${articleSlug || 'Page Not Found'}`
    )
    const body = encodeURIComponent(`
I encountered a 404 error on the following page:
URL: ${window.location.href}
Error: ${error}
Article: ${articleSlug || 'N/A'}
Time: ${new Date().toISOString()}
Device: ${isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop'}

Please investigate this issue.
    `)
    window.open(`mailto:support@gcctaxlaws.com?subject=${subject}&body=${body}`)
  }

  const isLawRelated = location.pathname.includes('/laws/') || articleSlug

  return (
    <AnimatedContainer>
      {/* Floating background elements - only on larger screens */}
      {!isMobile && (
        <>
          <FloatingElement sx={{ top: '15%', left: '15%' }} delay={0}>
            <GavelIcon sx={{ fontSize: '3rem' }} />
          </FloatingElement>
          <FloatingElement sx={{ top: '20%', right: '20%' }} delay={2}>
            <DocumentIcon sx={{ fontSize: '2.5rem' }} />
          </FloatingElement>
          <FloatingElement sx={{ bottom: '20%', left: '20%' }} delay={4}>
            <ArticleIcon sx={{ fontSize: '3.5rem' }} />
          </FloatingElement>
          <FloatingElement sx={{ bottom: '25%', right: '15%' }} delay={6}>
            <BookIcon sx={{ fontSize: '2.8rem' }} />
          </FloatingElement>
        </>
      )}

      <StyledPaper elevation={0}>
        {/* Broken 404 with enhanced connection effect */}
        <BrokenErrorContainer>
          <ErrorDigit position='first' ismobile={isMobile}>
            4
          </ErrorDigit>
          <Box sx={{ position: 'relative' }}>
            <ConnectionLine ismobile={isMobile} position='first' />
            <ElectricalDischarge ismobile={isMobile} />
          </Box>
          <ErrorDigit position='zero' ismobile={isMobile}>
            0
          </ErrorDigit>
          <Box sx={{ position: 'relative' }}>
            <ConnectionLine ismobile={isMobile} position='second' />
            <ElectricalDischarge ismobile={isMobile} />
          </Box>
          <ErrorDigit position='last' ismobile={isMobile}>
            4
          </ErrorDigit>
        </BrokenErrorContainer>

        {/* Warning icon for mobile */}
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <WarningIcon sx={{ fontSize: '3rem', color: '#ffcf51' }} />
          </Box>
        )}

        {/* Title and Description */}
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{
            fontWeight: 700,
            mb: 2,
            color: '#232536',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
          }}
        >
          {customTitle ||
            (isLawRelated ? 'Legal Document Not Found' : 'Page Not Found')}
        </Typography>

        <Typography
          variant={isMobile ? 'body1' : 'h6'}
          color='text.secondary'
          sx={{
            mb: 3,
            lineHeight: 1.6,
            fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' },
            maxWidth: '500px',
            mx: 'auto',
          }}
        >
          {isLawRelated
            ? `The requested document could not be found in our database.`
            : "The page you're looking for seems to have been moved or doesn't exist."}
          {/* {isLawRelated
            ? `The requested legal document "${
                articleSlug || 'content'
              }" could not be found in our database.`
            : "The page you're looking for seems to have been moved or doesn't exist."} */}
        </Typography>

        {/* Error details chip */}
        {articleSlug && (
          <Chip
            icon={<BugReportIcon />}
            label={`Document: ${articleSlug}`}
            variant='outlined'
            sx={{
              mb: 3,
              borderColor: '#ffcf51',
              color: '#232536',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}
          />
        )}

        {/* Action Buttons - Responsive layout */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 0 },
          }}
        >
          <ActionButton
            variant='primary'
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            fullWidth={isMobile}
          >
            Go Back
          </ActionButton>

          {/* <ActionButton
            variant='secondary'
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            fullWidth={isMobile}
          >
            Refresh
          </ActionButton> */}

          <ActionButton
            variant='outline'
            startIcon={<BugReportIcon />}
            onClick={handleReportIssue}
            fullWidth={isMobile}
          >
            Report Issue
          </ActionButton>
        </Box>

        {/* Error Details (if enabled) */}
        {showErrorDetails && error && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                variant='subtitle2'
                sx={{ fontWeight: 600, mb: 1, color: '#232536' }}
              >
                Technical Details:
              </Typography>
              <Typography
                variant='body2'
                color='error'
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                  p: 2,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  wordBreak: 'break-all',
                }}
              >
                {error}
              </Typography>
            </Box>
          </>
        )}

        {/* Suggestions */}
        {showSuggestions && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              sx={{
                fontWeight: 600,
                mb: 3,
                color: '#232536',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              What would you like to do?
            </Typography>

            <Grid container spacing={2} justifyContent='center'>
              {suggestions.map((suggestion, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  // lg={3}
                  // Mobile (xs): 1 item per row (12/12)
                  // Small (sm): 2 items per row (6/12 each)
                  // Medium (md): 3 items per row (4/12 each)
                  // Large (lg+): All 5 items in one row (2.4/12 each ≈ 20% width)
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    // maxWidth: { lg: '240px' },
                  }}
                >
                  <SuggestionCard
                    onClick={suggestion.action}
                    sx={{ width: '100%'
                    // , maxWidth: '200px'
                     }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2, sm: 2.5 },
                        textAlign: 'center',
                        '&:last-child': { pb: { xs: 2, sm: 2.5 } },
                      }}
                    >
                      <Box
                        className='suggestion-icon'
                        sx={{
                          color: suggestion.color,
                          mb: 1.5,
                          display: 'flex',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {React.cloneElement(suggestion.icon, {
                          sx: { fontSize: { xs: '2rem', sm: '2.5rem' } },
                        })}
                      </Box>
                      <Typography
                        variant='subtitle1'
                        sx={{
                          fontWeight: 600,
                          mb: 0.5,
                          color: '#232536',
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                        }}
                      >
                        {suggestion.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        {suggestion.description}
                      </Typography>
                    </CardContent>
                  </SuggestionCard>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* Law-specific help */}
        {isLawRelated && (
          <Box
            sx={{
              mt: 3,
              p: { xs: 2, sm: 2.5 },
              bgcolor: alpha('#232536', 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha('#232536', 0.1)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <HelpIcon sx={{ color: '#232536', mr: 1 }} />
              <Typography
                variant='subtitle2'
                sx={{
                  fontWeight: 600,
                  color: '#232536',
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                }}
              >
                Looking for legal content?
              </Typography>
            </Box>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              Try using our Lex search to find similar documents, or browse the
              law hierarchy to locate the content you need.
            </Typography>
          </Box>
        )}
      </StyledPaper>
    </AnimatedContainer>
  )
}

// Enhanced error handler for your use case
export const Enhanced404Handler = ({
  error,
  article,
  showFullDetails = false,
}) => {
  const is404Error =
    error?.includes('404') ||
    error?.includes('Not Found') ||
    error?.includes('not found')

  if (!is404Error) {
    return (
      <Box sx={{ padding: 2, margin: 2 }}>
        <Typography color='error' variant='h6'>
          Error loading {article?.slug} content:
        </Typography>
        <Typography color='error'>{error}</Typography>
      </Box>
    )
  }

  return (
    <Error404
      error={error}
      articleSlug={article?.slug}
      customTitle='Document Not Found'
      showSuggestions={true}
      showErrorDetails={showFullDetails}
    />
  )
}

export default Error404
